<script lang="ts">
    import { onDestroy, onMount } from "svelte";

    interface Props {
        minWidth: number;
        maxWidth: number;
        currentWidth: number;
        onResize: (width: number) => void;
    }

    let { minWidth, maxWidth, currentWidth, onResize }: Props = $props();

    let dragHandle: HTMLElement;

    let isDragging = false;
    let startX: number;
    let startWidth: number;

    const startDragging = (e: MouseEvent | TouchEvent) => {
        isDragging = true;
        startX = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX;
        startWidth = currentWidth;

        document.addEventListener("mousemove", handleDragging, { passive: true });
        document.addEventListener("mouseup", stopDragging, { passive: true });
        document.addEventListener("touchmove", handleDragging, { passive: true });
        document.addEventListener("touchend", stopDragging, { passive: true });
    };

    function handleDragging(e: MouseEvent | TouchEvent) {
        if (!isDragging) return;

        const clientX = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX;
        let deltaX = startX - clientX;
        if (document.documentElement.dir === "rtl") {
            deltaX = -deltaX;
        }
        const newWidth = startWidth + deltaX;
        onResize(Math.min(Math.max(newWidth, minWidth), maxWidth));
    }

    function stopDragging() {
        isDragging = false;
        document.removeEventListener("mousemove", handleDragging);
        document.removeEventListener("mouseup", stopDragging);
        document.removeEventListener("touchmove", handleDragging);
        document.removeEventListener("touchend", stopDragging);
    }

    onMount(() => {
        dragHandle.addEventListener("mousedown", startDragging, { passive: true });
        dragHandle.addEventListener("touchstart", startDragging, { passive: true });
    });

    onDestroy(() => {
        document.removeEventListener("mousemove", handleDragging);
        document.removeEventListener("mouseup", stopDragging);
        document.removeEventListener("touchmove", handleDragging);
        document.removeEventListener("touchend", stopDragging);

        dragHandle.removeEventListener("mousedown", startDragging);
        dragHandle.removeEventListener("touchstart", startDragging);
    });
</script>

<div
    bind:this={dragHandle}
    class="relative drag-handle my-auto mr-3 w-1.5 h-32 outline outline-0 outline-mblue bg-mlight cursor-col-resize transition-colors rounded-lg select-none"
></div>

<style>
    .drag-handle {
        pointer-events: auto;
    }

    /* We make the drag handle bigger than it really is to make it more easily selectable (especially on mobile) */
    .drag-handle:after {
        content: "";
        position: absolute;
        top: -10px;
        bottom: -10px;
        inset-inline-start: -10px;
        width: 25px;
        border-radius: 4px;
    }
    .drag-handle {
        box-shadow:
            1px 1px 1px 0px #5f6fff26,
            -2px -2px 4px 0px #5f6fff0d inset,
            3px 3px 4px 0px #ffffff inset;
    }
</style>
